import type { NavigateFunction } from "react-router-dom";

import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface OrganizationListItem {
  id: number;
  slug: string;
  name: string;
  is_business: boolean;
  tier?: string | null; // tier B2B org : starter | business | enterprise
  subscription_type?: string | null;
  purchased_seats: number;
  active_seats: number;
  member_count: number;
  team_count: number;
  campaign_count: number;
  stripe_customer_id?: string | null;
  created_at?: string | null;
  converted_business_at?: string | null;
  // Accès offert (billing grant) actif — badge « Offert » de la liste.
  billing_grant_active?: boolean;
  billing_grant_tier?: string | null;
  billing_grant_ends_at?: string | null; // null + actif = illimité
}

export interface PaginatedOrganizationsResponse {
  items: OrganizationListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface OrganizationUserSummary {
  id: number;
  keycloak_sub: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface OrganizationMemberItem {
  id: number;
  user_id: number;
  role: string;
  joined_at: string;
  validated_at?: string | null;
  user: OrganizationUserSummary;
}

export interface OrganizationTeamMember {
  user_id: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface OrganizationTeamItem {
  id: number;
  name: string;
  created_at: string;
  member_count: number;
  members: OrganizationTeamMember[];
}

export interface OrganizationDetail {
  id: number;
  slug: string;
  name: string;
  is_business: boolean;
  tier?: string | null; // tier B2B org : starter | business | enterprise
  business_name?: string | null;
  business_address?: string | null;
  business_contact_email?: string | null;
  sso_idp_alias?: string | null;
  sso_idp_status?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  stripe_subscription_item?: string | null;
  purchased_seats: number;
  active_seats: number;
  subscription_type?: string | null;
  created_at?: string | null;
  converted_business_at?: string | null;
  member_count: number;
  team_count: number;
  campaign_count: number;
  members: OrganizationMemberItem[];
  teams: OrganizationTeamItem[];
}

export async function listOrganizations(
  navigate: NavigateFunction,
  params?: {
    page?: number;
    size?: number;
    search?: string;
    isBusiness?: boolean;
  }
): Promise<PaginatedOrganizationsResponse> {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  if (params?.search) q.set("search", params.search);
  if (params?.isBusiness !== undefined)
    q.set("is_business", String(params.isBusiness));
  const url = `${API_URL}/organizations?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des organisations");
  return res.json();
}

export async function getOrganization(
  navigate: NavigateFunction,
  organizationId: number | string
): Promise<OrganizationDetail> {
  const res = await apiFetch(
    `${API_URL}/organizations/${encodeURIComponent(String(organizationId))}`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Organisation introuvable");
  return res.json();
}

/**
 * État d'une invitation, tel que le support en a besoin.
 *
 * `last_email_ok` / `last_email_error` sont exactement ce qui manquait pour
 * traiter un ticket « mon collègue n'a jamais reçu son invitation » : ces
 * champs n'existaient que dans l'API client, réservée aux OrgAdmin de
 * l'organisation concernée.
 */
export interface OrganizationInvitationItem {
  id: number;
  member_id: number;
  email: string;
  status: string;
  created_at: string;
  last_sent_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  send_attempts: number;
  last_email_ok: boolean | null;
  last_email_error: string | null;
}

/**
 * Une TENTATIVE d'envoi. `outcome === "sent"` signifie que le relais SMTP a
 * accepté le message, pas que le destinataire l'a reçu.
 */
export interface OrganizationInvitationEmailItem {
  id: number;
  email: string;
  kind: string;
  outcome: string;
  error: string | null;
  sender_email: string | null;
  message_id: string | null;
  created_at: string;
}

export async function getOrganizationInvitations(
  navigate: NavigateFunction,
  organizationId: number | string
): Promise<OrganizationInvitationItem[]> {
  const res = await apiFetch(
    `${API_URL}/organizations/${encodeURIComponent(
      String(organizationId)
    )}/invitations`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des invitations");
  return res.json();
}

export async function getOrganizationInvitationEmails(
  navigate: NavigateFunction,
  organizationId: number | string,
  limit = 100
): Promise<OrganizationInvitationEmailItem[]> {
  const res = await apiFetch(
    `${API_URL}/organizations/${encodeURIComponent(
      String(organizationId)
    )}/invitation-emails?limit=${limit}`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement du journal d'envois");
  return res.json();
}

/**
 * Force-transfer (staff override) : promeut `toUserId` en unique OrgAdmin et
 * rétrograde tous les OrgAdmin actuels. Gated `check_user_roles(["admin"])`
 * côté backoffice. `reason` obligatoire (5–500 car.) pour l'audit.
 */
export async function forceTransferOwnership(
  navigate: NavigateFunction,
  organizationId: number | string,
  toUserId: number,
  reason: string
): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/organizations/${encodeURIComponent(
      String(organizationId)
    )}/force-transfer`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to_user_id: toUserId, reason }),
    },
    navigate
  );
  if (!res || (res.status !== 204 && !res.ok)) {
    let detail = "Erreur lors du transfert forcé";
    try {
      const data = await res?.clone().json();
      if (data?.detail)
        detail =
          typeof data.detail === "string"
            ? data.detail
            : JSON.stringify(data.detail);
    } catch {
      /* corps non-JSON : on garde le message par défaut */
    }
    throw new Error(detail);
  }
}

// --------------------------------------------------------------------------- //
// Statistiques B2B agrégées (dashboard admin) — GET /api/organizations/stats
// --------------------------------------------------------------------------- //

export interface OrgTierCount {
  tier: string;
  count: number;
}

export interface OrgRoleCount {
  role: string;
  count: number;
}

export interface SeatOccupancyBucket {
  label: string;
  count: number;
}

export interface OrgBusinessStats {
  total_orgs: number;
  business_orgs: number;
  personal_orgs: number;
  converted_business: number;
  at_risk_orgs: number;
  tiers: OrgTierCount[];
}

export interface OrgSeatStats {
  total_purchased_seats: number;
  total_active_seats: number;
  total_occupied_seats: number;
  occupancy_histogram: SeatOccupancyBucket[];
  onboarding_incomplete: number;
}

export interface OrgStructureStats {
  roles: OrgRoleCount[];
  pending_invitations: number;
  total_teams: number;
  total_campaigns_org_scoped: number;
}

export interface OrgSecurityStats {
  sso_configured_orgs: number;
  mfa_required_orgs: number;
}

export interface OrgMrrStats {
  mrr_cents: number;
  mrr_eur: number;
  currency: string;
  per_seat_orgs: number;
  legacy_excluded: boolean;
}

export interface OrganizationStatsResponse {
  business: OrgBusinessStats;
  seats: OrgSeatStats;
  structure: OrgStructureStats;
  security: OrgSecurityStats;
  mrr: OrgMrrStats;
  generated_at: string;
}

/** Props partagées des cartes du dashboard B2B (un seul fetch /stats en amont). */
export interface B2bStatsCardProps {
  stats: OrganizationStatsResponse | null;
  loading: boolean;
  error: string | null;
}

export interface AuditEventItem {
  id: number;
  organization_id: number;
  organization_name?: string | null;
  actor_email?: string | null;
  action: string;
  target_type?: string | null;
  target_id?: string | null;
  created_at: string;
}

export interface PaginatedAuditEventsResponse {
  items: AuditEventItem[];
  limit: number;
  has_more: boolean;
}

export async function getOrganizationStats(
  navigate: NavigateFunction
): Promise<OrganizationStatsResponse> {
  const res = await apiFetch(
    `${API_URL}/organizations/stats`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok)
    throw new Error("Erreur lors du chargement des statistiques B2B");
  return res.json();
}

export async function getOrganizationActivity(
  navigate: NavigateFunction,
  params?: { limit?: number; organizationId?: number; actionPrefix?: string }
): Promise<PaginatedAuditEventsResponse> {
  const q = new URLSearchParams();
  q.set("limit", String(params?.limit ?? 20));
  if (params?.organizationId)
    q.set("organization_id", String(params.organizationId));
  if (params?.actionPrefix) q.set("action_prefix", params.actionPrefix);
  const res = await apiFetch(
    `${API_URL}/organizations/activity?${q.toString()}`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement de l'activité B2B");
  return res.json();
}
